// EditTemplate.tsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { AddressItem, DateItem, GeneralItem, IntroductionItem, Item, NoteItem, Section, SongItem, TimelineItem } from './types';
import ItemEditor from './ItemEditor';

const EditTemplate: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [sections, setSections] = useState<Section[]>([]);
  const [itemType, setItemType] = useState<'song' | 'introduction' | 'timeline' | 'general' | 'address' | 'date' | 'note'>('song');

  useEffect(() => {
    fetchTemplate();
  }, [id]);

  const fetchTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/Templates/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { name, jsonContent } = response.data;
      setName(name);
      const parsedContent = JSON.parse(jsonContent || '{"sections": []}');
      const initializedSections = (parsedContent.sections || []).map((section: Section) => ({
        ...section,
        clientNote: section.clientNote || '',
        allowClientAdditions: section.allowClientAdditions ?? false,
        allowedItemTypes: section.allowedItemTypes ?? [],
        items: (section.items || []).map((item: Item) => {
          const baseItem = {
            ...item,
            notes: item.notes || '',
            clientNotes: item.clientNotes || '',
            isClientAdded: 'isClientAdded' in item ? item.isClientAdded : false,
          };
          switch (item.type) {
            case 'song':
              return { ...baseItem, label: (item as SongItem).label || '', song: (item as SongItem).song || '' };
            case 'introduction':
              return { ...baseItem, name: (item as IntroductionItem).name || '', song: (item as IntroductionItem).song || '' };
            case 'timeline':
              return { ...baseItem, event: (item as TimelineItem).event || '', time: (item as TimelineItem).time || '' };
            case 'general':
              return { ...baseItem, key: (item as GeneralItem).key || '', value: (item as GeneralItem).value || '' };
            case 'address':
              return {
                ...baseItem,
                label: (item as AddressItem).label || '',
                street: (item as AddressItem).street || '',
                city: (item as AddressItem).city || '',
                state: (item as AddressItem).state || '',
                zip: (item as AddressItem).zip || ''
              };
            case 'date':
              return {
                ...baseItem,
                label: (item as DateItem).label || '',
                date: (item as DateItem).date || '',
                time: (item as DateItem).time || ''
              };
            case 'note':
              return {
                ...baseItem,
                content: (item as NoteItem).content || '',
                height: (item as NoteItem).height || 100
              };
            default:
              return baseItem;
          }
        }),
      }));
      setSections(initializedSections);
    } catch (error) {
      console.error('Failed to fetch template:', error);
      alert('Failed to load template. Please try again.');
    }
  };

  const updateTemplate = async () => {
    const jsonContent = JSON.stringify({ sections });
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/Templates/${id}`,
        { name, jsonContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Template updated successfully!');
      navigate('/templates');
    } catch (error) {
      console.error('Failed to update template:', error);
      alert('Failed to update template. Please try again.');
    }
  };

  const addSection = () => {
    setSections([...sections, { 
      label: '', 
      note: '', 
      clientNote: '', 
      items: [], 
      allowClientAdditions: false, 
      allowedItemTypes: [] 
    }]);
  };

  const updateSectionString = (index: number, field: 'label' | 'note' | 'clientNote', value: string) => {
    const updatedSections = [...sections];
    updatedSections[index][field] = value;
    setSections(updatedSections);
  };

  const updateSectionBoolean = (index: number, field: 'allowClientAdditions', value: boolean) => {
    const updatedSections = [...sections];
    updatedSections[index][field] = value;
    setSections(updatedSections);
  };

  const updateSectionAllowedItemTypes = (index: number, itemType: 'song' | 'introduction' | 'timeline' | 'general' | 'address' | 'date' | 'note', checked: boolean) => {
    const updatedSections = [...sections];
    const currentTypes = updatedSections[index].allowedItemTypes;
    if (checked) {
      updatedSections[index].allowedItemTypes = [...currentTypes, itemType];
    } else {
      updatedSections[index].allowedItemTypes = currentTypes.filter(type => type !== itemType);
    }
    setSections(updatedSections);
  };

  const addItem = (sectionIndex: number) => {
    const updatedSections = [...sections];
    switch (itemType) {
      case 'song':
        updatedSections[sectionIndex].items.push({ type: 'song', label: '', song: '', notes: '', clientNotes: '' });
        break;
      case 'introduction':
        updatedSections[sectionIndex].items.push({ type: 'introduction', name: '', song: '', notes: '', clientNotes: '' });
        break;
      case 'timeline':
        updatedSections[sectionIndex].items.push({ type: 'timeline', event: '', time: '', notes: '', clientNotes: '' });
        break;
      case 'general':
        updatedSections[sectionIndex].items.push({ type: 'general', key: '', value: '', notes: '', clientNotes: '' });
        break;
      case 'address':
        updatedSections[sectionIndex].items.push({ type: 'address', label: '', street: '', city: '', state: '', zip: '', notes: '', clientNotes: '' });
        break;
      case 'date':
        updatedSections[sectionIndex].items.push({ type: 'date', label: '', date: '', time: '', notes: '', clientNotes: '' });
        break;
      case 'note':
        updatedSections[sectionIndex].items.push({ type: 'note', content: '', height: 100, notes: '', clientNotes: '' });
        break;
    }
    setSections(updatedSections);
  };

  const updateItem = (sectionIndex: number, itemIndex: number, field: string, value: string) => {
    const updatedSections = [...sections];
    updatedSections[sectionIndex].items[itemIndex] = { ...updatedSections[sectionIndex].items[itemIndex], [field]: value };
    setSections(updatedSections);
  };

  const deleteSection = (sectionIndex: number) => {
    setSections(sections.filter((_, i) => i !== sectionIndex));
  };

  const deleteItem = (sectionIndex: number, itemIndex: number) => {
    const updatedSections = [...sections];
    updatedSections[sectionIndex].items = updatedSections[sectionIndex].items.filter((_, i) => i !== itemIndex);
    setSections(updatedSections);
  };

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    if (!destination) return;
    if (source.droppableId !== destination.droppableId) return;
    if (source.index === destination.index) return;

    const sectionIndex = parseInt(source.droppableId.split('-')[1], 10);
    const updatedSections = [...sections];
    const sectionItems = [...updatedSections[sectionIndex].items];

    const [movedItem] = sectionItems.splice(source.index, 1);
    sectionItems.splice(destination.index, 0, movedItem);

    updatedSections[sectionIndex].items = sectionItems;
    setSections(updatedSections);
  };

  return (
    <div className="container mt-5">
      <h2 className="mb-4 text-center">Edit Template - {name} (ID: {id})</h2>
      <div className="card shadow-sm">
        <div className="card-header bg-primary text-white">
          <h4 className="mb-0">Template Details</h4>
        </div>
        <div className="card-body">
          <div className="mb-3">
            <label className="form-label">Name</label>
            <input
              type="text"
              className="form-control"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <DragDropContext onDragEnd={onDragEnd}>
            {sections.map((section, sectionIndex) => (
              <div key={`section-${sectionIndex}`} className="card mb-3 shadow-sm">
                <div className="card-header bg-secondary text-white d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Section {sectionIndex + 1}</h5>
                  <button
                    onClick={() => deleteSection(sectionIndex)}
                    className="btn btn-danger btn-sm"
                  >
                    Delete Section
                  </button>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <label className="form-label">Section Label</label>
                    <input
                      type="text"
                      className="form-control"
                      value={section.label}
                      onChange={(e) => updateSectionString(sectionIndex, 'label', e.target.value)}
                      placeholder="e.g., Ceremony"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Section Note (DJ)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={section.note}
                      onChange={(e) => updateSectionString(sectionIndex, 'note', e.target.value)}
                      placeholder="General note for this section"
                    />
                  </div>
                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={section.allowClientAdditions}
                        onChange={(e) => updateSectionBoolean(sectionIndex, 'allowClientAdditions', e.target.checked)}
                      />
                      <label className="form-check-label">
                        Allow clients to add items to this section
                      </label>
                    </div>
                  </div>
                  {section.allowClientAdditions && (
                    <div className="mb-3">
                      <label className="form-label">Allowed Item Types for Client Additions</label>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={section.allowedItemTypes.includes('song')}
                          onChange={(e) => updateSectionAllowedItemTypes(sectionIndex, 'song', e.target.checked)}
                        />
                        <label className="form-check-label">Song</label>
                      </div>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={section.allowedItemTypes.includes('introduction')}
                          onChange={(e) => updateSectionAllowedItemTypes(sectionIndex, 'introduction', e.target.checked)}
                        />
                        <label className="form-check-label">Introduction</label>
                      </div>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={section.allowedItemTypes.includes('timeline')}
                          onChange={(e) => updateSectionAllowedItemTypes(sectionIndex, 'timeline', e.target.checked)}
                        />
                        <label className="form-check-label">Timeline</label>
                      </div>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={section.allowedItemTypes.includes('general')}
                          onChange={(e) => updateSectionAllowedItemTypes(sectionIndex, 'general', e.target.checked)}
                        />
                        <label className="form-check-label">General</label>
                      </div>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={section.allowedItemTypes.includes('address')}
                          onChange={(e) => updateSectionAllowedItemTypes(sectionIndex, 'address', e.target.checked)}
                        />
                        <label className="form-check-label">Address</label>
                      </div>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={section.allowedItemTypes.includes('date')}
                          onChange={(e) => updateSectionAllowedItemTypes(sectionIndex, 'date', e.target.checked)}
                        />
                        <label className="form-check-label">Date</label>
                      </div>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={section.allowedItemTypes.includes('note')}
                          onChange={(e) => updateSectionAllowedItemTypes(sectionIndex, 'note', e.target.checked)}
                        />
                        <label className="form-check-label">Note</label>
                      </div>
                    </div>
                  )}
                  <h5>Items</h5>
                  <Droppable droppableId={`items-${sectionIndex}`}>
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.droppableProps}>
                        {section.items.map((item, itemIndex) => (
                          <Draggable
                            key={`${sectionIndex}-${itemIndex}`}
                            draggableId={`${sectionIndex}-${itemIndex}`}
                            index={itemIndex}
                          >
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className="draggable-item d-flex flex-column flex-md-row align-items-md-center mb-3 p-3 border rounded bg-light"
                              >
                                <ItemEditor
                                  item={item}
                                  sectionIndex={sectionIndex}
                                  itemIndex={itemIndex}
                                  updateItemField={updateItem}
                                />
                                <div className="d-flex align-items-center">
                                  <button
                                    onClick={() => deleteItem(sectionIndex, itemIndex)}
                                    className="btn btn-danger btn-sm"
                                  >
                                    Delete Item
                                  </button>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                  <div className="mb-3">
                    <label className="form-label">Item Type</label>
                    <select
                      className="form-select"
                      value={itemType}
                      onChange={(e) => setItemType(e.target.value as 'song' | 'introduction' | 'timeline' | 'general' | 'address' | 'date' | 'note')}
                    >
                      <option value="song">Song</option>
                      <option value="introduction">Introduction</option>
                      <option value="timeline">Timeline</option>
                      <option value="general">General</option>
                      <option value="address">Address</option>
                      <option value="date">Date</option>
                      <option value="note">Note</option>
                    </select>
                  </div>
                  <button
                    onClick={() => addItem(sectionIndex)}
                    className="btn btn-outline-primary btn-sm"
                  >
                    Add Item
                  </button>
                </div>
              </div>
            ))}
          </DragDropContext>
          <div className="d-flex justify-content-between mt-3">
            <button onClick={addSection} className="btn btn-primary">
              Add Section
            </button>
            <div>
              <button onClick={updateTemplate} className="btn btn-success me-2">
                Update Template
              </button>
              <button onClick={() => navigate('/templates')} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditTemplate;